import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  ApiPaginatedResponse,
  QuestionBankItem,
  QuestionSearchCriteria,
} from '../../interfaces/question-search';

@Injectable({ providedIn: 'root' })
export class QuestionSearchService {
  private readonly http = inject(HttpClient);
  // `BASE_URL` contient déjà `/api` : ne jamais le rajouter ici.
  private readonly base = `${environment.BASE_URL}/question-bank`;

  /** Recherche à facettes paginée. Toutes les facettes se combinent en ET côté back. */
  search(
    criteria: QuestionSearchCriteria = {},
  ): Observable<ApiPaginatedResponse<QuestionBankItem>> {
    return this.http.get<ApiPaginatedResponse<QuestionBankItem>>(
      `${this.base}/search`,
      { params: this.toHttpParams(criteria) },
    );
  }

  /**
   * File de relecture : uniquement les questions en attente, les plus anciennes
   * d'abord. Le back force `status`, `sortBy` et `sortDescending` — les fournir
   * dans `criteria` n'a aucun effet.
   */
  reviewQueue(
    criteria: QuestionSearchCriteria = {},
  ): Observable<ApiPaginatedResponse<QuestionBankItem>> {
    return this.http.get<ApiPaginatedResponse<QuestionBankItem>>(
      `${this.base}/review-queue`,
      { params: this.toHttpParams(criteria) },
    );
  }

  /**
   * Sérialise les critères en query string.
   *
   * Les valeurs vides sont écartées plutôt qu'envoyées : un `themeId=` reçu par
   * le back ne vaut pas « pas de filtre », il produit un filtre sur une valeur
   * vide. `false` et `0` sont en revanche des valeurs légitimes et transmises —
   * `currentVersionOnly=false` est le seul moyen de consulter l'historique.
   *
   * Les tableaux sont répétés (`themeIds=1&themeIds=2`), forme attendue par le
   * binding `[FromQuery] List<T>` d'ASP.NET Core ; une liste jointe par des
   * virgules serait interprétée comme une seule valeur.
   */
  private toHttpParams(criteria: QuestionSearchCriteria): HttpParams {
    let params = new HttpParams();

    for (const [key, value] of Object.entries(criteria)) {
      if (value === null || value === undefined) continue;

      if (Array.isArray(value)) {
        for (const item of value) {
          if (item === null || item === undefined || item === '') continue;
          params = params.append(key, String(item));
        }
        continue;
      }

      if (typeof value === 'string' && value.trim() === '') continue;

      params = params.set(key, String(value));
    }

    return params;
  }
}
